import React, { useEffect, useMemo, useState } from "react";
import { Download, Trash2, Filter, Mail, Phone, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const badgeStyle = (src) => {
  if (src === "landing-1") return "bg-[#FF5A1F]/10 text-[#FF5A1F]";
  if (src === "landing-2") return "bg-[#0A0A0A] text-[#FAFAF7]";
  return "bg-[#F2F2EC] text-[#0A0A0A]";
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return leads;
    return leads.filter((l) => l.source_page === filter);
  }, [leads, filter]);

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/leads/export/csv");
      const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `perla-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Esportato");
    } catch { toast.error("Errore export"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Confermi l'eliminazione del lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Eliminato");
    load();
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-leads-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Candidature</div>
          <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Lead</h1>
          <p className="text-[#0A0A0A]/60 mt-2">Le candidature ricevute dal sito e dalle landing.</p>
        </div>
        <button onClick={exportCsv} className="btn-cta" data-testid="admin-leads-export">
          <Download size={16} /> Esporta CSV
        </button>
      </div>

      <div className="mb-6 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-xs uppercase tracking-widest text-[#0A0A0A]/60 mr-2">
          <Filter size={14} /> Fonte:
        </div>
        {["all", "contact", "landing-1", "landing-2"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border-2 border-[#0A0A0A] ${filter === f ? "bg-[#0A0A0A] text-[#FAFAF7]" : "bg-[#FAFAF7] text-[#0A0A0A]"}`}
          >
            {f === "all" ? `Tutti (${leads.length})` : `${f} (${leads.filter((l) => l.source_page === f).length})`}
          </button>
        ))}
      </div>

      {loading ? <div className="text-[#0A0A0A]/50">Caricamento…</div> : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-[#0A0A0A]/30 p-12 text-center text-[#0A0A0A]/50">
          Nessun lead ancora. Appena qualcuno invia il form apparirà qui.
        </div>
      ) : (
        <div className="border-2 border-[#0A0A0A] overflow-x-auto bg-[#FAFAF7]">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-[#FAFAF7]">
              <tr>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Data</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Nome</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Email</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Fonte</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">UTM</th>
                <th className="text-right px-4 py-3 font-bold uppercase text-xs tracking-widest">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className="border-t border-[#0A0A0A]/10 hover:bg-[#F2F2EC] cursor-pointer" onClick={() => setDetail(l)} data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 text-xs text-[#0A0A0A]/60">{new Date(l.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-4 py-3 font-bold">{l.name}</td>
                  <td className="px-4 py-3 text-[#0A0A0A]/80">{l.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-bold ${badgeStyle(l.source_page)}`}>{l.source_page}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#0A0A0A]/60">{l.utm_campaign || l.utm_source || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); remove(l.id); }} className="text-[#0A0A0A]/40 hover:text-red-600" data-testid={`lead-del-${l.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="lead-detail-modal">
          <div className="bg-[#FAFAF7] max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#0A0A0A] text-[#FAFAF7] p-5 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#FF5A1F]">Lead da {detail.source_page}</div>
                <h3 className="font-display text-3xl uppercase">{detail.name}</h3>
              </div>
              <button onClick={() => setDetail(null)} className="text-[#FAFAF7]/60 hover:text-[#FF5A1F]">✕</button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid md:grid-cols-2 gap-4">
                <DetailRow icon={Mail} label="Email" value={<a href={`mailto:${detail.email}`} className="text-[#FF5A1F] underline">{detail.email}</a>} />
                <DetailRow icon={Phone} label="Telefono" value={detail.phone || "—"} />
                <DetailRow label="Nome d'arte" value={detail.artist_name || "—"} />
                <DetailRow label="Genere" value={detail.genre || "—"} />
                <DetailRow icon={Instagram} label="Instagram" value={detail.instagram || "—"} />
                <DetailRow label="Ascoltatori mensili" value={detail.monthly_listeners || "—"} />
                <DetailRow label="Obiettivo" value={detail.goal || "—"} />
                <DetailRow label="Budget" value={detail.budget || "—"} />
              </div>
              {detail.message && (
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60 mb-1">Messaggio</div>
                  <div className="border-2 border-[#0A0A0A]/20 p-3 bg-white">{detail.message}</div>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60 mb-2">Tracking UTM</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {["utm_source", "utm_campaign", "utm_medium", "utm_content", "utm_term", "utm_ad", "utm_adset", "utm_platform"].map((k) => (
                    <div key={k} className="flex justify-between border-b border-[#0A0A0A]/10 py-1">
                      <span className="text-[#0A0A0A]/60">{k}</span>
                      <span className="font-mono">{detail[k] || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DetailRow = ({ icon: Icon, label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60 flex items-center gap-1">
      {Icon && <Icon size={12} />} {label}
    </div>
    <div className="mt-1">{value}</div>
  </div>
);
